import React from 'react';
import { Calendar, Clock, AlertCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

interface ClienteEnPausa {
    id: string;
    title: string;
    date: string;
    reason: string;
    proximoAVolver: boolean;
    fechaVencida?: boolean;
}

interface ClientesEnPausaProps {
    todosEnPausa: ClienteEnPausa[];
    proximosAVolver: ClienteEnPausa[];
}

const ClientesEnPausa: React.FC<ClientesEnPausaProps> = ({ todosEnPausa }) => {
    const navigate = useNavigate();
    const { tenantCode } = useParams<{ tenantCode: string }>();
    
    // Contar clientes con fechas vencidas y próximos
    const clientesVencidos = todosEnPausa.filter(cliente => cliente.fechaVencida).length;
    const clientesProximos = todosEnPausa.filter(cliente => cliente.proximoAVolver && !cliente.fechaVencida).length;
    
    const handleClienteClick = (id: string) => {
        navigate(`/${tenantCode}/clientes/${id}`);
    };
    
    // Ordenar para mostrar primero los vencidos, luego los próximos, y finalmente los demás
    const clientesOrdenados = [...todosEnPausa].sort((a, b) => {
        // Primero los vencidos
        if (a.fechaVencida && !b.fechaVencida) return -1;
        if (!a.fechaVencida && b.fechaVencida) return 1;
        
        // Luego los próximos
        if (a.proximoAVolver && !b.proximoAVolver) return -1;
        if (!a.proximoAVolver && b.proximoAVolver) return 1;
        
        // Si ambos son del mismo tipo, ordenar por fecha
        return new Date(a.date.replace('Vuelve el ', '')).getTime() - 
                new Date(b.date.replace('Vuelve el ', '')).getTime();
    });
    
    return (
        <div className="bg-white rounded-lg shadow-md p-4 flex flex-col h-full">
            {/* Título mejorado para evitar saltos de línea */}
            <div className="flex flex-wrap items-center justify-between mb-2">
                <div className="flex items-center">
                    <Calendar className="mr-2" size={18} />
                    <h3 className="text-lg font-bold text-gray-800">
                        Clientes en pausa ({todosEnPausa.length})
                    </h3>
                </div>
                <div className="flex space-x-2 mt-1 sm:mt-0 mb-2">
                    {clientesVencidos > 0 && (
                        <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                            {clientesVencidos} vencidos
                        </span>
                    )}
                    {clientesProximos > 0 && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                            {clientesProximos} próximos
                        </span>
                    )}
                </div>
            </div>
            
            {/* Alertas */}
            <div className="space-y-2 mb-3">
                {clientesVencidos > 0 && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-2">
                        <div className="flex items-center">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            <p className="ml-2 text-xs text-red-700">
                                ¡Atención! Hay clientes cuya fecha de retorno ya pasó.
                            </p>
                        </div>
                    </div>
                )}
                
                {clientesProximos > 0 && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2">
                        <div className="flex items-center">
                            <AlertCircle className="h-4 w-4 text-yellow-400" />
                            <p className="ml-2 text-xs text-yellow-700">
                                Recuerda reanudar las plazas de los clientes al volver.
                            </p>
                        </div>
                    </div>
                )}
            </div>
            
            {todosEnPausa.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay clientes en pausa</p>
            ) : (
                <div className="flex-1 overflow-y-auto max-h-72">
                    <ul className="space-y-2">
                        {clientesOrdenados.map((cliente, index) => (
                            <li 
                                key={index} 
                                className={`flex items-start border-b border-gray-100 pb-2 cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors
                                    ${cliente.fechaVencida ? 'bg-red-50' : 
                                        cliente.proximoAVolver ? 'bg-yellow-50' : ''}`}
                                onClick={() => handleClienteClick(cliente.id)}
                            >
                                <div className={`rounded-full p-1 mr-2 flex-shrink-0
                                    ${cliente.fechaVencida ? 'bg-red-100 text-red-800' : 
                                        cliente.proximoAVolver ? 'bg-yellow-100 text-yellow-800' : 
                                                                    'bg-blue-100 text-blue-800'}`}
                                >
                                    {cliente.fechaVencida ? <AlertTriangle size={16} /> : 
                                        cliente.proximoAVolver ? <RefreshCw size={16} /> : 
                                                                    <Clock size={16} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{cliente.title}</p>
                                    <p className={`text-xs 
                                        ${cliente.fechaVencida ? 'text-red-600 font-bold' : 
                                            cliente.proximoAVolver ? 'text-yellow-600 font-medium' : 
                                                                        'text-gray-600'}`}
                                    >
                                        {cliente.date}
                                    </p>
                                    <p className="text-xs text-gray-500 italic truncate">{cliente.reason}</p>
                                </div>
                                {cliente.fechaVencida && (
                                    <div className="ml-auto flex-shrink-0">
                                        <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
                                            Vencido
                                        </span>
                                    </div>
                                )}
                                {!cliente.fechaVencida && cliente.proximoAVolver && (
                                    <div className="ml-auto flex-shrink-0">
                                        <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                                            Próximo
                                        </span>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ClientesEnPausa;